import { Autocomplete, Chip, TextField } from '@material-ui/core';
import useApi from 'src/api/api';
import { useLocale } from 'src/provider/LocaleProvider';
import { useQuery } from 'react-query';
import LoadingView from './LoadingView';
import { useEffect, useMemo } from 'react';

const _ = require('lodash');

const ClassSelector = ({
  default_id,
  selected,
  setSelected = new Function(),
  filters,
  autocomplete = true,
  all = true,
  multiple = false,
  hasUncategorized = false,
  type = 'all',
  ...rest
}) => {
  const { t, getLanguage } = useLocale();
  const api = useApi();
  const allOption = {
    name: {
      en: t('all_grade_and_class'),
      zh: t('all_grade_and_class')
    },
    attribute: null,
    value: null
  };
  const noneOption = {
    name: {
      en: t('uncategorized'),
      zh: t('uncategorized')
    },
    attribute: null,
    value: 'NONE'
  };

  // Preset options
  const presetOptions = useMemo(() => {
    const options = [];
    if (all) {
      options.push(allOption);
    }
    if (hasUncategorized) {
      options.push(noneOption);
    }
    return options;
  }, [all, hasUncategorized, t, getLanguage]);
  // const presetOptions = hasUncategorized ? [allOption, noneOption] : [allOption]
  const {
    isLoading,
    isError,
    data: filterList
  } = useQuery(['organization-filters'], async () => {
    if (filters) {
      return filters;
    }
    const resData = await api.getOrganizationFilters().then((res) => res.data);
    if (type === 'class' || type === 'grade') {
      return _.filter(resData, (item) => item.attribute === type);
    }
    return resData;
  });

  const getLabel = (option) => _.get(option, `name.${getLanguage()}`, '');
  // Combine preset + dynamic options
  const options = useMemo(() => {
    return _.concat(presetOptions, filterList);
  }, [presetOptions, filterList]);

  let selectedValue = _.filter(
    filterList,
    (item) => selected && _.includes(selected, item._id)
  );

  const defaultValue = all ? allOption : hasUncategorized ? noneOption : null;
  const calculatedValue = multiple
    ? selectedValue.length > 0
      ? selectedValue
      : [defaultValue]
    : selected || defaultValue;

  const handleMultiChange = (event, value, reason, details) => {
    // Check if the 'None' option was clicked
    if (details?.option?.value === null) {
      setSelected([]);
    } else {
      // Assumes setSelected expects an array of IDs
      setSelected(_.map(value, (item) => item._id));
    }
  };

  const handleSingleChange = (event, value) => {
    // Assumes setSelected expects the single selected object
    setSelected(value);
  };

  const autocompleteProps = {
    size: multiple ? 'medium' : 'small',
    openOnFocus: true,
    autoHighlight: true,
    disableClearable: true,
    options,
    getOptionLabel: getLabel,
    value: calculatedValue,
    isOptionEqualToValue: (option, value) => option?._id === value?._id,
    onChange: multiple ? handleMultiChange : handleSingleChange,
    renderInput: (params) => (
      <TextField
        {...params}
        label={t('grade_or_class')}
        variant="outlined"
        InputLabelProps={{ shrink: true }}
      />
    ),
    style: { minWidth: '150px' },
    ...rest
  };

  if (isLoading || isError) {
    return <LoadingView />;
  }

  if (multiple) {
    return (
      <Autocomplete
        multiple
        renderTags={(tagValue, getTagProps) => {
          return tagValue.map((option, index) => {
            return (
              <Chip
                variant="outlined"
                label={_.get(option, `name.${getLanguage()}`, '')}
                {...getTagProps({ index })}
              />
            );
          });
        }}
        {...autocompleteProps}
      />
    );
  }

  return <Autocomplete {...autocompleteProps} />;
};
export default ClassSelector;
